import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import DevLogin from './components/DevLogin/DevLogin';
import { currencies as hardcodedCurrencies } from './constants';
import { CHINA_VERSION, env } from './globals';
import ScriptLoader from './helpers/ScriptLoader/ScriptLoader';
import useOnetrustActiveGroups from './helpers/ScriptLoader/hooks/useOneTrustActiveGroups';
import devLoginCheck from './helpers/devLoginCheck';
import getCountryCode from './helpers/get-country-code';
import useAxios from './hooks/useAxios/useAxios';
import useCheckReach5Session from './hooks/useCheckReach5Session/useCheckReach5Session';
import useProductCode from './hooks/useProductCode/useProductCode';
import {
  fetchExchanges,
  fetchLanguages,
  fetchStaticCnTranslations,
  setAdditionalPolicy,
  setCountryCode,
  setCurrencies,
  setHighestAgentCommissionEnabled,
  setloginEnabled,
  setNewsletterEnabled,
} from './redux/slices/appSettingsSlice/appSettingsSlice';
import {
  fetchHotels,
  setPhone,
} from './redux/slices/belmondSlice/belmondSlice';
import {
  resetBookingsState,
  setTempBookings,
} from './redux/slices/bookingsSlice/bookingsSlice';
import { resetGuestFormState } from './redux/slices/guestFormSlice/guestFormSlice';
import { fetchMedia } from './redux/slices/mediaSlice/mediaSlice';
import { resetPaymentState } from './redux/slices/paymentSlice/paymentSlice';
import { checkActiveAuthSession } from './redux/slices/userSlice/userSlice';

const isAuthCheckSkip = (param) => {
  if (param === 'false') {
    localStorage.removeItem('skipAuthCheck');
    return false;
  }

  let skip = localStorage.getItem('skipAuthCheck');
  if (skip === 'true') return true;

  if (param === 'true') {
    localStorage.setItem('skipAuthCheck', 'true');
    return true;
  }

  return false;
};

const Root = () => {
  useCheckReach5Session();
  const axios = useAxios();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const consents = useOnetrustActiveGroups();

  const [initialCallsMade, setInitialCallsMade] = useState(false);
  const [initChecksMade, setInitChecksMade] = useState(false);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);

  const countryCode = useSelector((state) => state.appSettings.countryCode);
  const bookings = useSelector((state) => state.bookings.list);
  const activeSpecialCode = useSelector(
    (state) => state.bookings.activeSpecialCode
  );
  const existingTabSession = sessionStorage.getItem('existingTabSession');
  const pageInTabHistory =
    sessionStorage.getItem('pageInTabHistory') === 'true';
  const currentBooking = useSelector((state) =>
    state.bookings.list.find((b) => b.id === state.bookings.current)
  );

  const bypassParam = searchParams.get('bypass');
  const skipAuthCheckParam = searchParams.get('skipAuthCheck');
  const discovery = searchParams.get('discovery');

  const productCode = useProductCode();

  // Replace EIL with SAF in productCode before any async calls
  // this should reload the page

  if (productCode === 'EIL' || productCode === 'SEL') {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('productCode', 'SAF');
    const newUrl = `${window.location.pathname}?${newSearchParams.toString()}`;
    window.location.replace(newUrl);
  }

  // Replace EIL with SAF in productCode before any async calls
  // this should reload the page

  if (productCode === 'EIL' || productCode === 'SEL') {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('productCode', 'SAF');
    const newUrl = `${window.location.pathname}?${newSearchParams.toString()}`;
    window.location.replace(newUrl);
  }

  const isDevLoginValid =
    JSON.parse(localStorage.getItem('validUser'))?.valid ||
    env === 'prod' ||
    env === 'local' ||
    bypassParam === 'diamond';

  const fetchCountryCode = useCallback(async () => {
    if (CHINA_VERSION) {
      dispatch(setCountryCode('CN'));
    } else {
      const result = await getCountryCode();
      dispatch(setCountryCode(result));
    }
  }, [dispatch]);

  const fetchMetadata = useCallback(async () => {
    if (!countryCode) return;

    try {
      const res = await axios.get('/metadata', {
        params: { country_code: countryCode },
      });

      dispatch(setPhone(res.data.phone));
      dispatch(setloginEnabled(res.data.loginEnabled));
      dispatch(setNewsletterEnabled(res.data.newsletterEnabled));
      dispatch(
        setHighestAgentCommissionEnabled(res.data.highestAgentCommissionEnabled)
      );
      dispatch(setAdditionalPolicy(res.data.bbeAdditionalPolicy));
    } catch (e) {
      console.error(e?.message || e);
    }
  }, [axios, dispatch, countryCode]);

  useEffect(() => {
    if (bypassParam === 'diamond') {
      window.localStorage.setItem(
        'validUser',
        JSON.stringify({
          valid: true,
          timestamp: Date.now(),
        })
      );
    }
  }, [bypassParam]);

  useEffect(() => {
    const selectingRoom = location.pathname === '/select-room';
    if (
      location.pathname === '/confirmation' ||
      (selectingRoom &&
        ((currentBooking && currentBooking.status !== 'UNSUBMITTED') ||
          (bookings.length && bookings[0].productCode !== productCode)))
    ) {
      dispatch(resetGuestFormState());
      dispatch(resetBookingsState());
      dispatch(resetPaymentState());
    } else if (selectingRoom && !bookings.length) {
      dispatch(resetBookingsState());
    }
    setInitChecksMade(true);
  }, [
    currentBooking,
    dispatch,
    bookings,
    bookings.length,
    location.pathname,
    productCode,
  ]);

  useEffect(() => {
    const initCalls = async () => {
      // axios changes based on pathname
      // so we need a state to track initial calls to avoid
      // to avoid multiple calls
      if (initialCallsMade) return;

      try {
        await fetchCountryCode();
      } catch (e) {
        // ignore - falls back to GB inside getCountryCode
      } finally {
        const promises = [
          fetchMetadata(),
          dispatch(fetchExchanges(axios)),
          dispatch(fetchLanguages(axios)),
          dispatch(fetchHotels(axios, countryCode)),
          dispatch(fetchMedia(axios)),
        ];

        if (CHINA_VERSION) {
          promises.push(dispatch(fetchStaticCnTranslations(axios)));
        }

        await Promise.all(promises);

        setInitialCallsMade(true);
        dispatch(setCurrencies(hardcodedCurrencies));
        setLoading(false);
        devLoginCheck();
      }
    };

    if (scriptsLoaded) {
      initCalls();
    }
  }, [
    fetchMetadata,
    dispatch,
    scriptsLoaded,
    axios,
    fetchCountryCode,
    countryCode,
    productCode,
    initialCallsMade,
  ]);

  useEffect(() => {
    const skipAuthCheck = isAuthCheckSkip(skipAuthCheckParam);
    if (
      skipAuthCheck ||
      !!discovery ||
      ['COMP', 'DISC'].includes(activeSpecialCode)
    )
      return;
    dispatch(checkActiveAuthSession());
  }, [dispatch, skipAuthCheckParam, discovery, activeSpecialCode]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location]);

  useEffect(() => {
    //We want to set pageInTabHistory true when user leaves the page but stays on same tab
    const handleBeforeUnload = () => {
      sessionStorage.setItem('pageInTabHistory', true);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!existingTabSession || pageInTabHistory) {
      const isCheckoutPage = location.pathname === '/checkout';
      if (bookings.length > 0) {
        if (isCheckoutPage) {
          navigate(`/select-room?productCode=${bookings[0].productCode}`, {
            replace: true,
          });
        }
        dispatch(setTempBookings(bookings));
        dispatch(resetBookingsState());
      }

      sessionStorage.setItem('existingTabSession', true);
      sessionStorage.setItem('pageInTabHistory', null);
    }
  }, [
    existingTabSession,
    location,
    navigate,
    bookings,
    dispatch,
    pageInTabHistory,
  ]);

  useEffect(() => {
    const isDY = consents.includes('C0003');
    if (window.DYO?.ActiveConsent?.updateConsentAcceptedStatus) {
      window.DYO.ActiveConsent.updateConsentAcceptedStatus(isDY);
    }
  }, [consents]);

  return (
    <>
      <ScriptLoader onScriptsLoad={() => setScriptsLoaded(true)} />
      {initChecksMade &&
        (isDevLoginValid ? (
          <Outlet context={{ scriptsLoaded, metadataLoaded: !loading }} />
        ) : (
          <DevLogin />
        ))}
    </>
  );
};

export default Root;
